import React, { useCallback, Dispatch, SetStateAction } from 'react';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { Update as UpdateIcon } from '@mui/icons-material';
import { convertToPanelIndex, TabGroup } from './tabIndexUtils';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import General from './General/General';
import Description from './Description/Description';
import Template from './Template/Template';
import Questions from './Questions/Questions';
import Sources from './Sources/Sources';
import CandidateAttachments from '../Job/CandidateAttachments/CandidateAttachments';
import AutoStatuses from './Automation/AutoStatuses/AutoStatuses';
import TimeBased from './Automation/StatusTransitions/TimeBased';
import AutoForward from './Automation/AutoForward/AutoForward';
import AutoReply from './Automation/AutoReply/AutoReply';
import AutoSMSReply from './Automation/AutoSMSReply/AutoSMSReply';
import AutoSMSForward from './Automation/AutoSMSForward/AutoSMSForward';
import AutoTagging from './Automation/AutoTag/AutoTagging';
import Revolve from './Advanced/Revolve/Revolve';
import GoogleConversionScript from './Advanced/GoogleConversionScript/GoogleScript';
import PanelWorkflow from './Advanced/PanelWorkflow/PanelWorkflow';
import AIStudioSettings from './Advanced/AIStudio/AIStudioSettings';
import RequisitionInfo from './Advanced/RequisitionInfo/RequisitionInfo';
import TestGrid from './Advanced/TestGrid/TestGrid';
import HRQuestions from '../EditJob/HRQuestions/HRQuestions';
import { IJob, IJobData } from './types';
import { classes } from './styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  group?: number;
}

export default function EditJobTabContent({
  editTabValue,
  setEditTabValue,
  changeTab,
  setChangeTab,
  job,
  jobId,
  generalTabJobData,
  setGeneralTabJobData,
  descriptionTabJobData,
  setDescriptionTabJobData,
  templateTabJobData,
  setTemplateTabJobData,
  sourcesTabJobData,
  setSourcesTabJobData,
  sourceListIds,
  setSourceListIds,
  candidateAttachments,
  setCandidateAttachments,
  jobFeedActive,
  handleUpdateJob,
  setIsEdited,
  isUpdating,
  lastSavedFormat,
  panelWorkflowTabJobData,
  setPanelWorkflowTabJobData,
  canAddIndeedDemographicQ,
  jobLoading,
  setSnackbar,
  handleEditTabChange,
  revolveTabJobData,
  setRevolveTabJobData,
  googleScriptTabJobData,
  setGoogleScriptTabJobData,
  setChanges
}: {
  editTabValue: number;
  setEditTabValue: (value: number) => void;
  changeTab: [boolean, null | number];
  setChangeTab: Dispatch<SetStateAction<[boolean, null | number]>>;
  job: IJob | null;
  jobId: number;
  generalTabJobData: IJobData | null;
  setGeneralTabJobData: Dispatch<SetStateAction<IJobData | null>>;
  descriptionTabJobData: {
    summary: { error: string; required: boolean; value: string };
    description: { error: string; required: boolean; value: string };
  };
  setDescriptionTabJobData: Dispatch<
    SetStateAction<{
      summary: { error: string; required: boolean; value: string };
      description: { error: string; required: boolean; value: string };
    }>
  >;
  templateTabJobData: {
    template_enabled: boolean;
    template: { error: string; required: boolean; value: string };
  };
  setTemplateTabJobData: Dispatch<
    SetStateAction<{
      template_enabled: boolean;
      template: { error: string; required: boolean; value: string };
    }>
  >;
  sourcesTabJobData: {
    sources: { error: string; required: boolean; value: IJob['sources'] };
  } | null;
  setSourcesTabJobData: Dispatch<
    SetStateAction<{ sources: { error: string; required: boolean; value: IJob['sources'] } } | null>
  >;
  sourceListIds: number[];
  setSourceListIds: Dispatch<SetStateAction<number[]>>;
  candidateAttachments: { label: string; required: boolean; value: string }[];
  setCandidateAttachments: Dispatch<
    SetStateAction<{ label: string; required: boolean; value: string }[]>
  >;
  jobFeedActive: boolean;
  handleUpdateJob: () => void;
  setIsEdited: Dispatch<SetStateAction<boolean>>;
  isUpdating: boolean;
  lastSavedFormat: (job: IJob) => string;
  panelWorkflowTabJobData: {
    panel_chairs: {
      error: string;
      required: boolean;
      value: { id: number; firstname: string; lastname: string; email: string }[];
    };
    panel_members: {
      error: string;
      required: boolean;
      value: { id: number; firstname: string; lastname: string; email: string }[];
    };
    panel_observers: {
      error: string;
      required: boolean;
      value: { id: number; firstname: string; lastname: string; email: string }[];
    };
  } | null;
  setPanelWorkflowTabJobData: Dispatch<
    SetStateAction<{
      panel_chairs: {
        error: string;
        required: boolean;
        value: { id: number; firstname: string; lastname: string; email: string }[];
      };
      panel_members: {
        error: string;
        required: boolean;
        value: { id: number; firstname: string; lastname: string; email: string }[];
      };
      panel_observers: {
        error: string;
        required: boolean;
        value: { id: number; firstname: string; lastname: string; email: string }[];
      };
    } | null>
  >;
  canAddIndeedDemographicQ: boolean;
  jobLoading: boolean;
  setSnackbar: Dispatch<
    SetStateAction<{
      message: string;
      state: 'success' | 'warning' | 'error';
    }>
  >;
  handleEditTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  revolveTabJobData: {
    revolve_position_title: {
      error: string;
      required: boolean;
      value: string;
    };
  } | null;
  setRevolveTabJobData: Dispatch<
    SetStateAction<{
      revolve_position_title: {
        error: string;
        required: boolean;
        value: string;
      };
    } | null>
  >;
  googleScriptTabJobData: {
    google_conversion_script: {
      error: string;
      required: boolean;
      value: string;
    };
  } | null;
  setGoogleScriptTabJobData: Dispatch<
    SetStateAction<{
      google_conversion_script: {
        error: string;
        required: boolean;
        value: string;
      };
    } | null>
  >;
  setChanges: Dispatch<SetStateAction<string[]>>;
}) {
  const TabPanel = useCallback((props: TabPanelProps) => {
    const { children, value, index, group, ...other } = props;

    const resolvedIndex = group === undefined ? index : convertToPanelIndex(index, group);
    return (
      <div
        role="tabpanel"
        hidden={value !== resolvedIndex}
        id={`vertical-tabpanel-${resolvedIndex}`}
        aria-labelledby={`vertical-tab-${resolvedIndex}`}
        {...other}
      >
        {value === resolvedIndex && <Box>{children}</Box>}
      </div>
    );
  }, []);

  if (!job) return null;

  return (
    <Box sx={{ marginLeft: '3rem', width: '100%', overflow: 'auto' }}>
      <TabPanel value={editTabValue} index={0} group={TabGroup.JOB_INFORMATION}>
        <General
          jobId={jobId}
          changeTab={changeTab}
          setEditTabValue={setEditTabValue}
          setChangeTab={setChangeTab}
          newJobData={generalTabJobData}
          setNewJobsData={setGeneralTabJobData}
          handleUpdateJob={handleUpdateJob}
          setIsEdited={setIsEdited}
          jobFeedActive={jobFeedActive}
          setSnackbar={setSnackbar}
          setChanges={setChanges}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={1} group={TabGroup.JOB_INFORMATION}>
        <Description
          descriptionTabJobData={descriptionTabJobData}
          setDescriptionTabJobData={setDescriptionTabJobData}
          setIsEdited={setIsEdited}
          aiPrefillData={{
            title: job?.title,
            country: job?.location
          }}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={2} group={TabGroup.JOB_INFORMATION}>
        <Template
          templateTabJobData={templateTabJobData}
          setTemplateTabJobData={setTemplateTabJobData}
          setIsEdited={setIsEdited}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={3} group={TabGroup.JOB_INFORMATION}>
        <Questions jobId={jobId} job={job} canAddIndeedDemographicQ={canAddIndeedDemographicQ} />
      </TabPanel>
      <TabPanel value={editTabValue} index={4} group={TabGroup.JOB_INFORMATION}>
        <Sources
          job={job}
          sourcesTabJobData={sourcesTabJobData}
          setSourcesTabJobData={setSourcesTabJobData}
          sourceListIds={sourceListIds}
          setSourceListIds={setSourceListIds}
          setIsEdited={setIsEdited}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={0} group={TabGroup.CANDIDATE_SETTINGS}>
        <CandidateAttachments
          job={job}
          candidateAttachments={candidateAttachments}
          setCandidateAttachments={setCandidateAttachments}
          setIsEdited={setIsEdited}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={0} group={TabGroup.AUTOMATION}>
        <AutoStatuses {...{ jobId, setSnackbar, handleEditTabChange }} />
      </TabPanel>
      <TabPanel value={editTabValue} index={1} group={TabGroup.AUTOMATION}>
        <TimeBased jobId={jobId} setSnackbar={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={2} group={TabGroup.AUTOMATION}>
        <AutoForward jobId={jobId} setSnackbar={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={3} group={TabGroup.AUTOMATION}>
        <AutoReply jobId={jobId} setSnackbar={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={4} group={TabGroup.AUTOMATION}>
        <AutoSMSReply jobId={jobId} setSnackbar={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={5} group={TabGroup.AUTOMATION}>
        <AutoSMSForward jobId={jobId} setSnackbar={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={0} group={TabGroup.ADVANCED}>
        <RequisitionInfo
          jobId={jobId}
          setSnackbar={setSnackbar}
          isJobTemplate={job.state === 'template'}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={1} group={TabGroup.ADVANCED}>
        <HRQuestions applicationQuestionTemplateId={job.application_question_template.id} />
      </TabPanel>
      <TabPanel value={editTabValue} index={2} group={TabGroup.ADVANCED}>
        <TestGrid jobId={jobId} setSnackbar={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={3} group={TabGroup.ADVANCED}>
        <Revolve
          revolveTabJobData={revolveTabJobData}
          setRevolveTabJobData={setRevolveTabJobData}
          setIsEdited={setIsEdited}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={4} group={TabGroup.ADVANCED}>
        <GoogleConversionScript
          googleScriptTabJobData={googleScriptTabJobData}
          setGoogleScriptTabJobData={setGoogleScriptTabJobData}
          setIsEdited={setIsEdited}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={5} group={TabGroup.ADVANCED}>
        <PanelWorkflow
          jobId={jobId}
          panelWorkflowTabJobData={panelWorkflowTabJobData}
          setPanelWorkflowTabJobData={setPanelWorkflowTabJobData}
          setIsEdited={setIsEdited}
        />
      </TabPanel>
      <TabPanel value={editTabValue} index={6} group={TabGroup.ADVANCED}>
        <AIStudioSettings jobId={jobId} setSnackbarState={setSnackbar} />
      </TabPanel>
      <TabPanel value={editTabValue} index={0} group={TabGroup.NEW_FEATURES}>
        <AutoTagging {...{ jobId, setSnackbar, handleEditTabChange }} />
      </TabPanel>
      {jobLoading ? (
        <Box />
      ) : (
        <Stack alignItems="end" sx={{ padding: 1, width: '100%' }}>
          <Button
            id="save-job-questions-button"
            data-testid="save-job-questions-button"
            type="submit"
            sx={{
              ...sharedClasses.saveButton
            }}
            onClick={() => {
              if (editTabValue === 1) {
                return setChangeTab([false, 1]);
              }
              !isUpdating && handleUpdateJob();
            }}
          >
            {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
          <Stack sx={classes.lastSaved}>
            {isUpdating ? (
              <>
                <CircularProgress size={12} color="inherit" sx={{ marginRight: 1 }} />
                Saving
              </>
            ) : (
              <>
                <UpdateIcon sx={{ margin: '4px', fontSize: '1rem' }} />
                Last saved {lastSavedFormat(job)}
              </>
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
